import loginLayout from '../layouts/login';
import builderLayout from '../layouts/builder';
import previewLayout from '../layouts/preview';
import interviewLayout from '../layouts/interview';
import JobUnavailableLayout from '../layouts/job-unavailable.vue';

import { setAuthState } from '../composables/auth';
import { isAutoApplyQueueEnabled } from '../composables/autoapply-queue';

const requireFeature = (feature, { redirect = 'home', allowAnonymous = false } = {}) => () => {
	const { userHaveAccessTo, isAuthenticated } = setAuthState();
	if (allowAnonymous && !isAuthenticated()) {
		return;
	}
	if (!userHaveAccessTo(feature)) {
		return { name: redirect };
	}
};

// auth pages
const LoginPage = () => import('../pages/auth/login');
const RegisterPage = () => import('../pages/auth/register');
const ForgotPasswordPage = () => import('../pages/auth/forgot-password');
const ResetPasswordPage = () => import('../pages/auth/reset-password');

// app pages
const HomePage = () => import('../pages/home.vue');
const OrganizationDashboardPage = () => import('../pages/organization-dashboard.vue');
const CoachClientsPage = () => import('../pages/coach-clients.vue');
const ResumesPage = () => import('../pages/resumes');
const ResumeExamplesPage = () => import('../pages/resume-examples.vue');
const CoverLettersPage = () => import('../pages/cover-letters');
const SettingsPage = () => import('../pages/settings');
const ProfilePage = () => import('../pages/profile.vue');
const CheckoutPage = () => import('../pages/checkout.vue');
const NetworkingToolPage = () => import('../pages/networking-tool');
const NetworkingToolFavoritesPage = () => import('../pages/networking-tool-favorites');
const ResumeToolPage = () => import('../pages/resume-tool');
const BuilderResume = () => import('../pages/builder-resume');
const BuilderCoverLetter = () => import('../pages/builder-cover-letter');
const Errors404Page = () => import('../pages/errors/404');
const ExtensionLogin = () => import('../pages/extension-login');
const ServicesTrackingPage = () => import('../pages/services-tracking');
const JobTrackerPageV2 = () => import('../pages/job-tracker-page-v2.vue');
const AutoApplySubmissionReviewPage = () => import('../pages/auto-apply/submission-review-page.vue');

const JobsTrackingDataManagementPage = () => import('../pages/jobs-tracker-management-page.vue');
const AdminView = () => import('../pages/groups-page');
const SubgroupsPage = () => import('../pages/subgroups-page');
const SubgroupTablePage = () => import('../pages/subgroup-table');
const CreateInterviewPage = () => import('../pages/interview-ai/create-interview-page');
const InterviewAiDashboard = () => import('../pages/interview-ai/dashboard');
const InterviewAiInterviews = () => import('../pages/interview-ai/interviews-page');
const InterviewAiInterview = () => import('../pages/interview-ai/interview-page');
const InterviewAiCompleteInterview = () => import('../pages/interview-ai/interview-complete-page');
const LearningHubCourses = () => import('../pages/learning-hub/online-courses.vue');
const CoursePage = () => import('../pages/learning-hub/course.vue');

// job board imports
// const JobBoardAiOnboarding = () => import("../pages/job-board/onboarding.vue")
const JobBoardAiSearch = () => import('../pages/job-board/search-jobs.vue');
const JobBoardAiUnavailableJob = () => import('../pages/job-board/job-unavailable.vue');
const JobBoardAiAlerts = () => import('../pages/job-board/job-alerts.vue');
const CommunityRequests = () => import('../pages/job-board/community-requests.vue');

// job board v3 imports
const JobBoardV3Search = () => import('../jobboard-v3/pages/search-jobs.vue');
const JobBoardV3AutoApplyQueue = () => import('../jobboard-v3/pages/autoapply-queue.vue');
const JobBoardV3Saved = () => import('../jobboard-v3/pages/saved-jobs.vue');

const CommunityDashboard = () => import('../pages/community/dashboard.vue');
const CommunitySavedResumes = () => import('../pages/community/saved-resumes.vue');
const CommunityProfile = () => import('../pages/community/profile.vue');
const DocumentNotFoundPage = () => import('../pages/share/document-not-found.vue');

const routes = [
	{
		path: '/extension-login',
		name: 'extension-login',
		component: ExtensionLogin
	},

	// auth — meta.inertia names the Inertia section that takes these over when it is switched on
	{
		path: '/login/:slug?',
		name: 'login',
		component: LoginPage,
		meta: {
			layout: loginLayout,
			inertia: 'login'
		}
	},
	{
		path: '/register',
		name: 'register',
		component: RegisterPage,
		meta: {
			layout: loginLayout,
			onboardingStep: 1,
			inertia: 'register'
		}
	},
	{
		path: '/register/:slug',
		name: 'register-partner',
		component: RegisterPage,
		meta: {
			layout: loginLayout,
			onboardingStep: 1
		}
	},
	{
		path: '/forgot-password/:slug?',
		name: 'forgot-password',
		component: ForgotPasswordPage,
		meta: { layout: loginLayout }
	},
	{
		path: '/reset-password/:token',
		name: 'reset-password',
		component: ResetPasswordPage,
		meta: { layout: loginLayout }
	},

	// checkout — where a landing-page purchase link lands straight after sign-up, before onboarding
	{
		path: '/checkout',
		name: 'checkout',
		component: CheckoutPage,
		meta: {
			requiresAuth: true,
			layout: loginLayout,
			// Global prompts would stack on, or take over, the checkout modal.
			suppressGlobalPrompts: true,
		}
	},

	// onboarding — always hand off to the Inertia onboarding flow (/onboarding/v3)
	{
		path: '/onboarding',
		name: 'onboarding',
		component: { render: () => null },
		meta: { requiresAuth: true },
		beforeEnter: () => {
			const { redirectIntoOnboardingV3 } = setAuthState();
			redirectIntoOnboardingV3();
			return false;
		}
	},

	// dashboard
	{
		path: '/',
		name: 'home',
		component: HomePage,
		meta: {
			requiresAuth: true,
			title: 'WonsultingAI',
			feature: 'resume_upload'
		},
	},

	// organization dashboard (org admin view)
	{
		path: '/organization-dashboard',
		name: 'organization-dashboard',
		component: OrganizationDashboardPage,
		meta: {
			requiresAuth: true,
			title: 'Organization Dashboard',
		},
		beforeEnter: () => {
			const { getUser } = setAuthState();
			if (!getUser()?.admin_organization) {
				return { name: 'home' };
			}
		},
	},
	// Coach Client View (EN-1198). No role guard: the API answers a non-coach with 403 and the page
	// says it isn't available. The name must not start with `job-tracker` or `learning-hub`, which
	// would set the coach's own job search blockers on every visit.
	{
		path: '/coach/clients',
		name: 'coach-clients',
		component: CoachClientsPage,
		meta: {
			requiresAuth: true,
			title: 'My Clients',
		},
	},

	// building resume 
	{
		path: '/resumes',
		name: 'resumes',
		component: ResumesPage,
		meta: {
			requiresAuth: true,
			title: 'WonsultingAI',
			feature: 'resume_upload'
		},
	},
	{
		path: '/resume-tool',
		name: 'resume-tool',
		component: ResumeToolPage,
		beforeEnter: requireFeature('resume-tool', { redirect: 'resumes.edit' }),
		meta: {
			requiresAuth: false,
			title: 'WonsultingAI',
			feature: 'bullet_generation_tool'
		},
	},
	{
		path: '/resumes/not-found',
		name: 'resume-not-found',
		component: DocumentNotFoundPage,
		meta: {
			requiresAuth: false,
			title: 'Resume not found | Wonsulting',
			feature: 'resumes',
			isPreview: true,
			layout: loginLayout
		},
	},
	{
		path: '/resumes/examples',
		name: 'resume-examples',
		component: ResumeExamplesPage,
		meta: {
			requiresAuth: true,
			title: 'WonsultingAI',
			feature: 'resume_examples',
			coverLetter: false
		},
	},
	{
		path: '/resume/:id?',
		name: 'resumes.edit',
		component: BuilderResume,
		meta: {
			requiresAuth: false,
			layout: builderLayout,
			title: 'WonsultingAI',
			feature: 'bullet_generation_tool',
			coverLetter: false
		},
	},
	{
		path: '/resume/preview/:id?',
		name: 'resumes.preview',
		component: BuilderResume,
		meta: {
			requiresAuth: false,
			isPreview: true,
			layout: previewLayout,
			coverLetter: false
		},
	},

	// cover letter 
	{
		path: '/cover-letters',
		name: 'cover-letters.index',
		component: CoverLettersPage,
		meta: {
			title: 'CoverLetterAI by Wonsulting',
			coverLetter: true,
			feature: 'cover_letter'
		},
	},
	{
		path: '/cover-letter/:id?',
		name: 'cover-letters.edit',
		component: BuilderCoverLetter,
		meta: {
			requiresAuth: true,
			layout: builderLayout,
			coverLetter: true,
			feature: 'cover_letter',
			title: 'CoverLetterAI by Wonsulting',
		},
	},

	// settings 
	{
		path: '/settings',
		name: 'settings',
		component: SettingsPage,
		meta: {
			requiresAuth: true,
			title: 'WonsultingAI'
		},
		// The Profile tab moved to its own page (EN-940).
		beforeEnter: (to) => {
			const { userHaveAccessTo } = setAuthState();
			if (to.query?.tab === 'profile' && userHaveAccessTo('profile-v2')) {
				return { name: 'profile' };
			}
		},
	},

	// profile
	{
		path: '/profile',
		name: 'profile',
		component: ProfilePage,
		meta: {
			requiresAuth: true,
			title: 'WonsultingAI'
		},
		beforeEnter: requireFeature('profile-v2', { redirect: 'settings' }),
	},

	// service tracker 
	{
		path: '/services-tracker',
		name: 'services-tracker',
		component: ServicesTrackingPage,
		beforeEnter: requireFeature('client-portal'),
		meta: {
			requiresAuth: true,
			title: 'Services Tracking by Wonsulting'
		}
	},

	// auto apply submission review (must precede the catch-all redirect below)
	{
		path: '/auto-apply/submissions/:uuid/review',
		name: 'auto-apply-submission-review',
		component: AutoApplySubmissionReviewPage,
		meta: {
			requiresAuth: true,
			title: 'Review Application by Wonsulting',
			feature: 'job_tracker'
		}
	},
	// auto apply (redirects to job board)
	{
		path: '/auto-apply/:pathMatch(.*)*',
		redirect: '/job-board/search',
	},
	// job tracker
	{
		path: '/job-tracker',
		name: 'job-tracker',
		component: JobTrackerPageV2,
		meta: {
			requiresAuth: true,
			title: 'JobTrackerAI by Wonsulting',
			feature: 'job_tracker'
		}
	},
	{
		path: '/job-tracker/data-management',
		name: 'job-tracker-data-management',
		component: JobsTrackingDataManagementPage,
		meta: {
			requiresAuth: true,
			title: 'JobTrackerAI by Wonsulting',
			feature: 'job_tracker'
		}
	},

	// interview ai
	{ 
		path: '/interview-ai',
		name: 'interview-ai',
		component: InterviewAiDashboard,
		meta: {
			title: 'InterviewAI by Wonsulting',
			feature: 'interview'
		}
	},
	{
		path: '/interview-ai/interviews',
		name: 'interview-ai-interviews',
		component: InterviewAiInterviews,
		meta: {
			requiresAuth: true,
			title: 'InterviewAI by Wonsulting',
			feature: 'interview'
		}
	},
	{
		path: '/interview-ai/create',
		name: 'interview-ai-create',
		component: CreateInterviewPage,
		meta: {
			title: 'InterviewAI by Wonsulting',
			feature: 'interview'
		}
	},
	{
		path: '/interview-ai/interview/:interviewId/round/:roundId',
		name: 'interview-ai-interview',
		component: InterviewAiInterview,
		meta: {
			requiresAuth: true,
			layout: interviewLayout,
			title: 'InterviewAI by Wonsulting',
			feature: 'interview'
		}
	},
	{
		path: '/interview-ai/complete',
		name: 'interview-ai-complete',
		component: InterviewAiCompleteInterview,
		meta: {
			requiresAuth: true,
			title: 'InterviewAI by Wonsulting',
			feature: 'interview'
		}
	},
    
	//networking tool
	{
		path: '/networking-tool',
		name: 'networking-tool',
		component: NetworkingToolPage,
		meta: {
			title: 'NetworkAI by Wonsulting',
			feature: 'network_tool'
		},
	},
	{
		path: '/networking-tool/favorites',
		name: 'networking-tool-favorites',
		component: NetworkingToolFavoritesPage,
		meta: {
			requiresAuth: true,
			title: 'NetworkAI by Wonsulting',
			feature: 'network_tool'
		},
	},

	// job board
	// {
	//     path: '/job-board/onboarding',
	//     name: 'job-board-onboarding',
	//     component: JobBoardAiOnboarding,
	//     meta: {
	//         requiresAuth: true,
	//         title: 'JobBoardAI by Wonsulting',
	//         feature: 'job_board'
	//     },
	// },
	{
		path: '/job-board-classic/search',
		name: 'job-board-classic-search',
		component: JobBoardAiSearch,
		meta: {
			requiresAuth: false,
			title: 'JobBoardAI by Wonsulting',
			feature: 'job_board'
		},
	},
	{
		path: '/job-board/search',
		name: 'job-board-search',
		component: JobBoardV3Search,
		meta: {
			requiresAuth: false,
			title: 'JobBoardAI by Wonsulting',
			feature: 'job_board'
		},
	},
	// Preserve links to the pre-release v3 URL.
	{
		path: '/jobboard-v3/search',
		redirect: { name: 'job-board-search' },
	},
	{
		path: '/job-board/saved',
		name: 'job-board-saved',
		component: JobBoardV3Saved,
		meta: {
			requiresAuth: true,
			title: 'JobBoardAI by Wonsulting',
			feature: 'job_board'
		},
	},
	{
		path: '/job-board/autoapply',
		name: 'job-board-autoapply',
		component: JobBoardV3AutoApplyQueue,
		beforeEnter: () => (isAutoApplyQueueEnabled() ? undefined : { name: 'job-board-search' }),
		meta: {
			requiresAuth: true,
			title: 'JobBoardAI by Wonsulting',
			feature: 'job_board'
		},
	},
	{
		path: '/job-board/job-unavailable',
		name: 'job-board-job-unavailable',
		component: JobBoardAiUnavailableJob,
		meta: {
			requiresAuth: true,
			title: 'JobBoardAI by Wonsulting',
			feature: 'job_board',
			layout: JobUnavailableLayout
		},
	},
	{
		path: '/job-board/job-alerts',
		name: 'job-board-alerts',
		component: JobBoardAiAlerts,
		meta: {
			requiresAuth: true,
			title: 'JobBoardAI by Wonsulting',
			feature: 'job_board'
		},
	},
	{
		path: '/job-board/community-requests',
		name: 'job-board-community-requests',
		component: CommunityRequests,
		beforeEnter: requireFeature('community-requests', { redirect: 'job-board-search' }),
		meta: {
			requiresAuth: true,
			title: 'JobBoardAI by Wonsulting',
			feature: 'job_board'
		},
	},
    
	// community
	{
		path: '/community',
		name: 'community',
		component: CommunityDashboard,
		beforeEnter: requireFeature('resume-share', { allowAnonymous: true }),
		meta: {
			requiresAuth: false,
			title: 'Community | Wonsulting',
			feature: 'community',
			isPreview: true,
		},
	},
	{
		path: '/community/saved',
		name: 'community-saved',
		component: CommunitySavedResumes,
		beforeEnter: requireFeature('resume-share'),
		meta: {
			requiresAuth: true,
			title: 'Community | Saved Resumes | Wonsulting',
			feature: 'community',
			isPreview: true,
		},
	},
	{
		path: '/community/profile',
		name: 'community-profile',
		component: CommunityProfile,
		beforeEnter: requireFeature('resume-share'),
		meta: {
			requiresAuth: true,
			title: 'Community | My Profile | Wonsulting',
			feature: 'community',
			isPreview: true,
		},
	},
	// admin group tool
	{
		path: '/admin-view/groups',
		name: 'admin-view',
		component: AdminView,
		meta: {
			title: 'Admin View'
		},
	},
	{
		path: '/admin-view/groups/:groupId',
		name: 'admin-view-subgroups',
		component: SubgroupsPage,
		meta: {
			title: 'Admin View Subgroups'
		},
	},
	{
		path: '/admin-view/groups/:groupId/subgroups/:subgroupId',
		name: 'admin-view-subgroup-table',
		component: SubgroupTablePage,
		meta: {
			title: 'Admin View Subgroup'
		},
	},
    
	//learning hub start
	{
		path: '/learning-hub/courses',
		name: 'learning-hub-courses',
		component: LearningHubCourses,
		meta: {
			title: 'Learning Hub Courses'
		},
	},
	{
		path: '/learning-hub/course/:id',
		name: 'learning-hub-course',
		component: CoursePage,
		meta: {
			title: 'Learning Hub Course'
		},
	},
    
	// the help section was removed - keep old links out of the 404 page
	{
		path: '/help/:pathMatch(.*)*',
		redirect: { name: 'learning-hub-courses' },
	},

	// not found routes
	{
		path: '/:pathMatch(.*)*',
		name: '404',
		meta: { layout: loginLayout },
		component: Errors404Page,
	},
];

export default routes;
